import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { STATUS_META, formatMoney, formatDateTime, ROLE_LABEL } from "../lib/constants";
import StatusBadge from "../components/StatusBadge";
import { ArrowRight, Lightning, ClockCounterClockwise, CheckCircle, Warning, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_FOCUS = {
  sales: ["pending_admin", "cancelled_by_admin", "pending_finance", "awaiting_next_payment"],
  admin: ["pending_admin", "payment_confirmed", "refund_pending", "in_warehouse"],
  finance: ["pending_finance", "payment_confirmed", "refund_pending", "completed"],
  warehouse: ["in_warehouse", "delivered", "completed"],
  super_admin: ["pending_admin", "pending_finance", "refund_pending", "in_warehouse"],
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const requests = [
          api.get("/dashboard/stats"),
          api.get("/orders", { params: { limit: 8 } }),
        ];
        if (user.role === "admin" || user.role === "super_admin") {
          requests.push(api.get("/dashboard/heatmap"));
        }
        const results = await Promise.all(requests);
        setStats(results[0].data);
        setRecent(results[1].data);
        if (results[2]) setHeatmap(results[2].data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const focus = ROLE_FOCUS[user.role] || ROLE_FOCUS.super_admin;

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <header className="mb-10">
        <div className="overline mb-2">// {ROLE_LABEL[user.role]} Dashboard</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Hello, {user.name.split(" ")[0]}.
        </h1>
        <p className="text-[var(--muted)] mt-2 max-w-2xl">
          Today is {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}.
          Here is what is moving through your pipeline.
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <KpiCard
          accent
          icon={<Lightning size={20} weight="fill" color="#2563EB" />}
          label="Orders Today"
          value={stats?.today_count ?? "–"}
          testid="kpi-today"
        />
        <KpiCard
          icon={<ClockCounterClockwise size={20} weight="bold" />}
          label="Total Pipeline"
          value={stats?.total_count ?? "–"}
          testid="kpi-total"
        />
        <KpiCard
          icon={<Warning size={20} weight="fill" color="#D97706" />}
          label="Awaiting Action"
          value={
            stats
              ? (stats.by_status.pending_admin?.count || 0) +
                (stats.by_status.pending_finance?.count || 0) +
                (stats.by_status.payment_confirmed?.count || 0) +
                (stats.by_status.in_warehouse?.count || 0)
              : "–"
          }
          testid="kpi-action"
        />
        <KpiCard
          icon={<CheckCircle size={20} weight="fill" color="#059669" />}
          label="Completed"
          value={stats?.by_status?.completed?.count ?? "–"}
          testid="kpi-completed"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {focus.map((s) => (
          <div key={s} className="grid-line p-5 bg-white" data-testid={`focus-${s}`}>
            <div className="flex items-center justify-between mb-3">
              <StatusBadge status={s} />
              <span className="font-display font-black text-3xl">
                {stats?.by_status?.[s]?.count ?? 0}
              </span>
            </div>
            <div className="text-xs text-[var(--muted)]">
              Total value: {formatMoney(stats?.by_status?.[s]?.total)}
            </div>
            <Link
              to={`/orders?status=${s}`}
              className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-widest text-[var(--solar)] hover:underline"
              data-testid={`focus-link-${s}`}
            >
              View queue <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ))}
      </section>

      {heatmap && heatmap.reps && heatmap.reps.length > 0 && (
        <section className="mb-10 grid-line bg-white" data-testid="heatmap-section">
          <div className="border-b border-[var(--line)] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChartBar size={18} weight="bold" color="#2563EB" />
              <div className="font-display font-bold">7-day sales rep heatmap</div>
            </div>
            <div className="overline text-[var(--muted)]">Submitted vs returned by admin</div>
          </div>
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-4 overline">Sales rep</th>
                  {heatmap.days.map((d) => (
                    <th key={d} className="text-center py-2 px-2 overline" style={{ fontSize: "0.6rem" }}>
                      {d.slice(5)}
                    </th>
                  ))}
                  <th className="text-right py-2 pl-4 overline">7d totals</th>
                </tr>
              </thead>
              <tbody>
                {heatmap.reps.map((rep) => (
                  <tr key={rep.id} className="border-t border-[var(--line)]" data-testid={`heatmap-rep-${rep.id}`}>
                    <td className="py-2 pr-4 font-medium">{rep.name}</td>
                    {rep.cells.map((c, i) => {
                      const ratio = c.submitted ? c.returned / c.submitted : 0;
                      const bg =
                        c.submitted === 0
                          ? "#F8FAFC"
                          : ratio === 0
                          ? "#DCFCE7"
                          : ratio <= 0.25
                          ? "#FEF3C7"
                          : ratio <= 0.5
                          ? "#FED7AA"
                          : "#FECACA";
                      return (
                        <td key={i} className="text-center px-1">
                          <div
                            className="mx-auto inline-flex flex-col items-center justify-center min-w-[44px] py-1.5 border border-[var(--line)]"
                            style={{ background: bg }}
                            title={`${c.submitted} submitted / ${c.returned} returned`}
                          >
                            <span className="font-bold leading-none">{c.submitted}</span>
                            {c.returned > 0 && (
                              <span className="text-[0.55rem] text-[var(--danger)] leading-none mt-0.5">
                                {c.returned} ret
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="py-2 pl-4 text-right">
                      <span className="font-display font-bold">{rep.total_submitted}</span>
                      <span className="text-[var(--muted)] mx-1">/</span>
                      <span className={rep.total_returned ? "text-[var(--danger)] font-bold" : "text-[var(--muted)]"}>
                        {rep.total_returned} ret
                      </span>
                      <div className="text-[0.6rem] text-[var(--muted)]">
                        {(rep.return_rate * 100).toFixed(0)}% return rate
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex items-center gap-2 text-[0.7rem] text-[var(--muted)]">
              <span>Cell colour:</span>
              <span className="inline-block w-3 h-3 border border-[var(--line)]" style={{ background: "#DCFCE7" }} /> clean
              <span className="inline-block w-3 h-3 border border-[var(--line)]" style={{ background: "#FEF3C7" }} /> ≤25% ret
              <span className="inline-block w-3 h-3 border border-[var(--line)]" style={{ background: "#FED7AA" }} /> ≤50% ret
              <span className="inline-block w-3 h-3 border border-[var(--line)]" style={{ background: "#FECACA" }} /> &gt;50% ret
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-2xl tracking-tight">Recent orders</h2>
          <Link to="/orders" className="overline text-[var(--solar)] hover:underline" data-testid="all-orders-link">
            All orders →
          </Link>
        </div>
        <div className="grid-line bg-white">
          {loading ? (
            <div className="p-8 text-center overline">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="p-8 text-center text-[var(--muted)]">No orders yet.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Sales Rep</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((o) => (
                  <tr key={o.id} data-testid={`row-${o.order_id}`}>
                    <td className="font-mono-tech text-xs">{o.order_id}</td>
                    <td className="font-medium">{o.customer.name}</td>
                    <td>{o.sales_rep_name}</td>
                    <td>{formatMoney(o.total_amount)}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="text-xs text-[var(--muted)]">{formatDateTime(o.created_at)}</td>
                    <td>
                      <Link to={`/orders/${o.id}`} className="text-xs font-bold uppercase tracking-widest text-[var(--solar)] hover:underline">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function KpiCard({ icon, label, value, accent, testid }) {
  return (
    <div className={`kpi-card ${accent ? "accent" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {icon}
      </div>
      <div className="font-display font-black text-4xl mt-3">{value}</div>
    </div>
  );
}
